export function getTrendColor(change?: number) {
  if (change === undefined) {
    return 'text-gray-600';
  }

  if (change < 0) return 'text-green-600';
  if (change > 0) return 'text-red-600';
  return 'text-gray-600';
}

export type SortOption = 'alphabetical' | 'price-asc' | 'price-desc';

export type UnaggregatedRowData = {
  depth: number;
  label: string;
  key: string;
  // lineItems: QuoteSheetLineItemDTO[];
  pricesByDate?: {
    date: string;
    priceFOB: number | null;
    pctChange?: number;
    priceQuoteSheetId: string;
  }[];
  children?: UnaggregatedRowData[] | SupplierRowData[] | AggregatedRowData[];
};

export type SupplierRowData = UnaggregatedRowData & {
  vendor: { id: string; name: string };
};

export type AggregatedRowData = UnaggregatedRowData & {
  aggregatedPricesByDate: {
    date: string;
    priceFOBAvg: number | null;
    priceFOBMed: number | null;
    priceFOBMin: number | null;
    priceFOBMax: number | null;
  }[];
};

export function isSupplierRowData(
  data: AggregatedRowData | UnaggregatedRowData | SupplierRowData,
): data is SupplierRowData {
  return 'vendor' in data;
}

export function isAggregatedRowData(
  data: AggregatedRowData | UnaggregatedRowData,
): data is AggregatedRowData {
  return 'aggregatedPricesByDate' in data;
}

export function isAggregatedRowDataArr(
  data: AggregatedRowData[] | SupplierRowData[] | UnaggregatedRowData[],
): data is AggregatedRowData[] {
  if (data.length === 0) {
    return false;
  }

  return 'aggregatedPricesByDate' in data[0];
}

export function isSupplierRowDataArr(
  data: AggregatedRowData[] | SupplierRowData[] | UnaggregatedRowData[],
): data is SupplierRowData[] {
  if (data.length === 0) {
    return false;
  }

  return 'vendor' in data[0];
}

export enum ProductVariantBreakdownTypes {
  /**
   * The ProductV2
   */
  PRODUCT = 'product',
  /**
   * An enumerated view of the variants, treating all of them as unique
   */
  ENUMERATED_VARIANT = 'enumerated_variant',
  /**
   * A recursive view of the variants, allowing displays of supersets of the product attributes beneath their parental subsets
   *
   * This ends up close to impossible to implement as calculating the "tree" is non-deterministic. A "sub"-variant can have 2
   * parents each with the same number of Set differences.
   */
  // RECURSIVE_VARIANT = 'recursive_variant',
  /**
   * A view of the supplier, treating all of their products as unique
   */
  SUPPLIER = 'supplier',
}

export const DEFAULT_PRODUCT_VARIANT_BREAKDOWN: ProductVariantBreakdownTypes[] =
  [
    ProductVariantBreakdownTypes.PRODUCT,
    ProductVariantBreakdownTypes.ENUMERATED_VARIANT,
    ProductVariantBreakdownTypes.SUPPLIER,
  ] as const;

/**
 * The valid children for each product variant breakdown type. This controls the possible sub-breakdowns that can be applied to a given breakdown.
 */
export const ProductVariantBreakdownValidChildren: Record<
  ProductVariantBreakdownTypes,
  ProductVariantBreakdownTypes[]
> = {
  [ProductVariantBreakdownTypes.PRODUCT]: [
    ProductVariantBreakdownTypes.ENUMERATED_VARIANT,
    ProductVariantBreakdownTypes.SUPPLIER,
  ],
  [ProductVariantBreakdownTypes.ENUMERATED_VARIANT]: [
    ProductVariantBreakdownTypes.SUPPLIER,
  ],
  [ProductVariantBreakdownTypes.SUPPLIER]: [
    ProductVariantBreakdownTypes.PRODUCT,
    ProductVariantBreakdownTypes.ENUMERATED_VARIANT,
  ],
};

export function isNextBreakdownValid(
  breakdownChain: ProductVariantBreakdownTypes[],
  nextBreakdown: ProductVariantBreakdownTypes,
) {
  if (breakdownChain.length === 0) return true;

  const lastBreakdown = breakdownChain[breakdownChain.length - 1];
  const isValidNextBreakdown =
    ProductVariantBreakdownValidChildren[lastBreakdown].includes(nextBreakdown);
  const isNextBreakdownPresentInChain = breakdownChain.includes(nextBreakdown);

  return isValidNextBreakdown && !isNextBreakdownPresentInChain;
}

export function getNextValidBreakdowns(
  breakdownChain: ProductVariantBreakdownTypes[],
) {
  return Object.values(ProductVariantBreakdownTypes).filter((type) => {
    return isNextBreakdownValid(breakdownChain, type);
  });
}

/**
 * Formats a number as currency with proper null/undefined handling
 * @param value - The number to format, can be null or undefined
 * @param options - Formatting options
 * @returns Formatted currency string or empty/dash display
 */
export function formatCurrency(
  value: number | null | undefined,
  options: {
    /** What to display when value is null/undefined. Default: 'empty' */
    emptyDisplay?: 'empty' | 'dash';
    /** Number of decimal places. Default: 2 */
    decimals?: number;
  } = {},
): string {
  const { emptyDisplay = 'empty', decimals = 2 } = options;

  if (value == null) {
    return emptyDisplay === 'dash' ? '$ -' : '';
  }

  return `$${value.toFixed(decimals)}`;
}

/**
 * Safely gets an array item with bounds checking
 * @param array - The array to access
 * @param index - The index to access
 * @returns The item at index or undefined if out of bounds or array is null/undefined
 */
export function safeArrayAccess<T>(
  array: T[] | null | undefined,
  index: number,
): T | undefined {
  if (!array || index < 0 || index >= array.length) {
    return undefined;
  }
  return array[index];
}
